import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';

import { PageBreadcrumbComponent } from '../../../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ConfirmDialogService } from '../../../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../../../shared/services/toast.service';
import { HasPermissionDirective } from '../../../../../shared/directives/has-permission.directive';
import { TicketsConfigApiService } from '../../../services/tickets-config-api.service';
import { TicketsCannedResponseDrawerService } from '../../../services/tickets-config-drawer.services';
import { TicketCannedResponse } from '../../../models/tickets-config.model';

@Component({
  selector: 'app-tickets-canned-responses-list',
  standalone: true,
  imports: [CommonModule, FormsModule, PageBreadcrumbComponent, HasPermissionDirective],
  templateUrl: './canned-responses-list.component.html'
})
export class TicketsCannedResponsesListComponent implements OnInit, OnDestroy {
  items: TicketCannedResponse[] = [];
  filtered: TicketCannedResponse[] = [];
  loading = false;
  error = '';

  filtroNombre = '';
  filtroSoloActivas = false;
  filtroAlcance: 'all' | 'shared' | 'private' = 'all';

  private readonly destroy$ = new Subject<void>();

  constructor(
    protected router: Router,
    private configApi: TicketsConfigApiService,
    private drawer: TicketsCannedResponseDrawerService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService
  ) {}

  /** Nombre del propietario que resuelve el backend (`ownerUserName`); nunca el id. */
  ownerName(item: TicketCannedResponse): string {
    if (item.intIdUserOwner == null) return '';
    return item.ownerUserName?.trim() || 'Usuario sin nombre disponible';
  }

  ngOnInit(): void {
    this.cargar();
    this.drawer.entitySaved$.pipe(takeUntil(this.destroy$)).subscribe(() => this.cargar());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  cargar(): void {
    this.loading = true;
    this.error = '';
    this.configApi.getCannedResponses().pipe(takeUntil(this.destroy$)).subscribe({
      next: (data) => {
        this.items = data.sort((a, b) => a.name.localeCompare(b.name));
        this.aplicarFiltros();
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar plantillas';
        this.loading = false;
      }
    });
  }

  aplicarFiltros(): void {
    const term = this.filtroNombre.trim().toLowerCase();
    this.filtered = this.items.filter((cr) => {
      if (term && !cr.name.toLowerCase().includes(term)) return false;
      if (this.filtroSoloActivas && !cr.isActive) return false;
      if (this.filtroAlcance === 'shared' && cr.intIdUserOwner != null) return false;
      if (this.filtroAlcance === 'private' && cr.intIdUserOwner == null) return false;
      return true;
    });
  }

  nuevo(): void {
    this.drawer.open();
  }

  editar(item: TicketCannedResponse): void {
    this.drawer.open({ id: item.id });
  }

  toggleActiva(item: TicketCannedResponse): void {
    this.configApi
      .updateCannedResponse(item.id, {
        name: item.name,
        bodyHtml: item.bodyHtml,
        bodyText: item.bodyText,
        languageCode: item.languageCode ?? undefined,
        intIdUserOwner: item.intIdUserOwner ?? undefined,
        isActive: !item.isActive
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.cargar(),
        error: (err) => {
          this.toast.error({
            title: 'Error',
            message: err.error?.message || 'No se pudo actualizar la plantilla'
          });
        }
      });
  }

  async eliminar(item: TicketCannedResponse): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar plantilla',
      message: `¿Eliminar la plantilla "${item.name}"?`
    });
    if (!ok) return;
    this.configApi.deleteCannedResponse(item.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.toast.deleted('Plantilla de respuesta', `Se eliminó "${item.name}" correctamente.`);
        this.cargar();
      },
      error: (err) => {
        this.toast.error({
          title: 'Error',
          message: err.error?.message || 'No se pudo eliminar la plantilla'
        });
      }
    });
  }

  volverATickets(): void {
    this.router.navigate(['/tickets']);
  }
}
